import React from 'react';

import { Box, Container } from '@mui/material';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import parse from 'html-react-parser';
import { Link } from 'react-router-dom';

import AnimatedSkeleton from '~/components/AnimatedSkeleton';
import IKImage from '~/components/IKImage/IKImage';
import { VideoPlayerNewsLayout } from '~/components/NewsItem/NewsVideoPlayerLayout';
import { SocialShare } from '~/components/SocialShare/SocialShare';
import StartFreeTrial from '~/components/StartFreeTrial/StartFreeTrial';
import Avatar from '~/components/UI/Avatar';
import FlatPaper from '~/components/UI/FlatPaper/FlatPaper';
import { HERO_IMG_SRC_SET } from '~/const';
import useAuth from '~/hooks/useAuth';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import ExpertInfo from '~/pages/SingleVideoPage/components/Expert/ExpertInfo';
import { buildExpertRoute } from '~/routes';
import {
  BREAKPOINTS_VALUES,
  LARGE_MARGIN_PX,
  MIDDLE_MARGIN_PX,
  SMALL_MARGIN_PX,
  SUBMIDDLE_MARGIN_PX,
  TINY_MARGIN_PX,
} from '~/theme';

const BLOCKS = [
  {
    layout: 'image',
    Component: ({ data }) => (
      <BlockWrapper data-testid="newsContentImage">
        <BlockImage
          className="blockImage"
          path={data.image}
          alt={data.alt}
          draggable={false}
          srcset={HERO_IMG_SRC_SET}
          sizes={`(max-width:${BREAKPOINTS_VALUES['sm']}px) 500px, (max-width:${BREAKPOINTS_VALUES['md']}px) 800px, (max-width:${BREAKPOINTS_VALUES['lg']}px) 1100px, 1200px`}
        />
        {data?.caption && <Typography align="center">{data.caption}</Typography>}
      </BlockWrapper>
    ),
  },
  {
    layout: 'text',
    Component: ({ data, videoIds }) => {
      const textContent = data?.text_content || '';
      const parts = textContent.split('%%%video%%%');

      return (
        <div data-testid="newsContentText">
          {parts.map((part, index) => (
            <div key={index} data-testid="newsContentTextPart">
              {parse(part)}
              {index !== parts.length - 1 && <VideoPlayerNewsLayout videoId={videoIds[index]} />}
            </div>
          ))}
        </div>
      );
    },
  },
];

const NewsContent = ({ expert, title, imageUrl, postContent, loading, postVideosIds }) => {
  const { t } = useTenantTranslation();
  const { isAuthenticated } = useAuth();

  if (loading) {
    return (
      <StyledContainer>
        <MainImage>
          <LoadingIntroductionImage xs="694px" />
        </MainImage>
        <Content>
          <LoadingTextLine xs="35px" />
          <LoadingTextLine xs="35px" />
          <LoadingTextLine xs="35px" />
          <LoadingTextLine xs="35px" />
          <LoadingTextLine xs="35px" />
          <LoadingTextLineShort xs="35px" />
        </Content>
      </StyledContainer>
    );
  }

  return (
    <StyledContainer>
      <MainImage>
        {imageUrl && (
          <IntroductionImage
            path={imageUrl}
            alt={title}
            draggable={false}
            srcset={[{ width: 292 }, { width: 640 }, { width: 760 }, { width: 780 }]}
            sizes={`(max-width:${BREAKPOINTS_VALUES['sm']}px) 292px, (max-width:${BREAKPOINTS_VALUES['md']}px) 640px, (max-width:${BREAKPOINTS_VALUES['lg']}px) 760px, 780px`}
          />
        )}
      </MainImage>
      <Content>
        {!isAuthenticated && <StartFreeTrial />}
        {postContent?.map((block) => {
          const desiredBlock = BLOCKS.find((item) => item.layout === block.layout);
          const Component:
            | (({ data, videoIds }: { data: any; videoIds: string[] }) => JSX.Element | null)
            | undefined = desiredBlock?.Component;

          if (!Component) return null;
          return <Component key={block.key} data={block.attributes} videoIds={postVideosIds} />;
        })}
      </Content>
      <ExpertsSection id="expertSection" data-testid="expertSection">
        <Link to={buildExpertRoute(expert?.slug)}>
          <StyledAvatar alt={expert?.name} src={expert?.avatar} size={'lg'} />
        </Link>
        <InfoWrapper>
          <ExpertSectionLabel>{t('page.news.aboutTheAuthor')}</ExpertSectionLabel>
          <ExpertInfo
            id={expert?.id}
            name={expert?.name}
            bio={expert?.description}
            link={buildExpertRoute(expert?.slug)}
          />
        </InfoWrapper>
      </ExpertsSection>
      <SocialContainer>
        <ShareText>{t('page.news.shareTitle', { title: title })}</ShareText>
        <SocialShare title={title} />
      </SocialContainer>
    </StyledContainer>
  );
};

export default NewsContent;

const createIntroductionImage = (tag) => styled(tag)`
  width: 100%;
  max-width: 100%;
  max-height: 225px;
  margin-bottom: ${MIDDLE_MARGIN_PX};
  border-radius: 5px;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    max-height: 694px;
    margin-left: 0;
  }
`;
const IntroductionImage = createIntroductionImage(IKImage);
const LoadingIntroductionImage = createIntroductionImage(AnimatedSkeleton);
const StyledContainer = styled(Container)`
  ${({ theme }) => theme.breakpoints.up('sm')} {
    max-width: 1090px;
  }
`;
const MainImage = styled(Box)`
  width: 100%;
  position: relative;
  z-index: 4;
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const Content = styled(Box)`
  width: 100%;
  margin: 0 auto 20px;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    max-width: 798px;
    margin-bottom: ${MIDDLE_MARGIN_PX};
  }

  & > p {
    font-size: 18px;
    line-height: 1.5;
    color: ${({ theme }) => theme.palette.common.gray};
    text-align: left;
    margin-top: 0;
    margin-bottom: ${MIDDLE_MARGIN_PX};
  }

  & q {
    font-size: 18px;
    font-style: italic;
    line-height: 30px;
    display: block;
    margin: ${MIDDLE_MARGIN_PX} 0;
    padding: ${TINY_MARGIN_PX} ${MIDDLE_MARGIN_PX};
    border-left: 4px solid ${({ theme }) => theme.palette.common.blue};
    quotes: none;
    & > b {
      display: block;
    }
  }

  & a {
    color: ${({ theme }) => theme.palette.common.violet};
  }

  & a:hover {
    text-decoration: underline;
  }

  & img:not(.blockImage) {
    width: 100%;
    height: auto;
    margin-bottom: 40px;
    border-radius: 5px;
  }
`;
const LoadingTextLine = styled(AnimatedSkeleton)`
  margin-bottom: ${TINY_MARGIN_PX};
`;
const LoadingTextLineShort = styled(AnimatedSkeleton)`
  max-width: 150px;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    max-width: 350px;
  }
`;
const SocialContainer = styled(Box)`
  width: 100%;
  margin: 0 auto ${LARGE_MARGIN_PX};
  border-radius: 5px;
  display: block;
  justify-content: space-between;
  align-items: center;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    max-width: 798px;
    display: flex;
  }
`;
const ShareText = styled(Typography)`
  font-size: 18px;
  color: ${({ theme }) => theme.palette.common.gray};
  text-align: center;
  margin-bottom: ${SUBMIDDLE_MARGIN_PX};

  ${({ theme }) => theme.breakpoints.up('sm')} {
    text-align: left;
    margin-bottom: 0;
  }
`;

const BlockWrapper = styled('div')`
  margin-bottom: ${MIDDLE_MARGIN_PX};

  & > p {
    margin-bottom: 0;
    margin-top: ${SMALL_MARGIN_PX};
    text-align: center;
  }
`;
const BlockImage = styled(IKImage)`
  width: 100%;
  height: auto;
  border-radius: 5px;
`;
const ExpertsSection = styled(FlatPaper)`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 auto ${MIDDLE_MARGIN_PX};

  ${({ theme }) => theme.breakpoints.up('md')} {
    align-items: flex-start;
    flex-direction: row;
    max-width: 798px;
  }
`;
const InfoWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;

  ${({ theme }) => theme.breakpoints.up('md')} {
    align-items: flex-start;
  }
`;
const ExpertSectionLabel = styled('span')`
  margin-bottom: ${TINY_MARGIN_PX};
  color: ${({ theme }) => theme.palette.common.gray};
  line-height: 1;
`;
const StyledAvatar = styled(Avatar)`
  ${({ theme }) => theme.breakpoints.up('md')} {
    margin-right: ${MIDDLE_MARGIN_PX};
  }
`;
